import { useState } from 'react'
import { Search, User, Check, Loader2 } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { distributorApi, SimpleUser, CrmebSupplier } from '@/api/distributors'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { showError } from '@/lib/error'

interface BindDistributorDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSuccess: () => void
}

export function BindDistributorDialog({ open, onOpenChange, onSuccess }: BindDistributorDialogProps) {
	const [userKeyword, setUserKeyword] = useState('')
	const [supplierKeyword, setSupplierKeyword] = useState('')
	const [selectedUser, setSelectedUser] = useState<SimpleUser | null>(null)
	const [selectedSupplier, setSelectedSupplier] = useState<CrmebSupplier | null>(null)
	const [submitting, setSubmitting] = useState(false)

	// 搜索后台用户
	const { data: users = [], isLoading: loadingUsers, refetch: refetchUsers } = useQuery({
		queryKey: ['distributors', 'users', userKeyword],
		queryFn: async () => {
			if (!userKeyword.trim()) return []
			return distributorApi.searchUsers(userKeyword)
		},
		enabled: !!userKeyword.trim(),
	})

	// 搜索 CRMEB 供应商（直接查询 eb_system_supplier 表）
	const { data: suppliers = [], isLoading: loadingSuppliers, refetch: refetchSuppliers } = useQuery({
		queryKey: ['distributors', 'suppliers', supplierKeyword],
		queryFn: async () => {
			if (!supplierKeyword.trim()) return []
			return distributorApi.searchCrmebSuppliers(supplierKeyword)
		},
		enabled: !!supplierKeyword.trim(),
	})

	// 手动触发搜索
	const handleSearchUsers = () => {
		if (userKeyword.trim()) {
			refetchUsers()
		}
	}

	const handleSearchSuppliers = () => {
		if (supplierKeyword.trim()) {
			refetchSuppliers()
		}
	}

	// 提交绑定
	const handleSubmit = async () => {
		if (!selectedUser || !selectedSupplier) return

		setSubmitting(true)
		try {
			await distributorApi.bind({
				user_id: selectedUser.id,
				crmeb_uid: selectedSupplier.id,  // 直接使用 eb_system_supplier.id
			})
			toast.success('绑定成功')
			onSuccess()
			handleClose()
		} catch (error) {
			console.error('绑定失败:', error)
			showError(error, '绑定失败')
		} finally {
			setSubmitting(false)
		}
	}

	// 关闭弹窗并重置状态
	const handleClose = () => {
		setUserKeyword('')
		setSupplierKeyword('')
		setSelectedUser(null)
		setSelectedSupplier(null)
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>绑定供应商</DialogTitle>
					<DialogDescription>
						选择后台用户并绑定到 CRMEB 供应商
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* 步骤 1: 选择后台用户 */}
					<div className="space-y-3">
						<Label className="flex items-center gap-2">
							<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">1</span>
							选择后台用户
						</Label>
						<div className="flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									placeholder="搜索用户名或手机号"
									value={userKeyword}
									onChange={(e) => setUserKeyword(e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
									className="pl-9"
								/>
							</div>
							<Button variant="outline" onClick={handleSearchUsers} disabled={loadingUsers}>
								{loadingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : '搜索'}
							</Button>
						</div>

						{users && users.length > 0 && (
							<ScrollArea className="h-[140px] rounded-md border">
								<div className="p-2 space-y-1">
									{users.map((user) => (
										<div
											key={user.id}
											onClick={() => setSelectedUser(user)}
											className={cn(
												"flex items-center gap-3 rounded-lg p-2 cursor-pointer transition-colors",
												selectedUser?.id === user.id
													? "bg-primary/10 border border-primary/30"
													: "hover:bg-muted"
											)}
										>
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
												<User className="h-4 w-4 text-muted-foreground" />
											</div>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium truncate">{user.nickname || user.username}</p>
												<p className="text-xs text-muted-foreground">{user.phone || user.email}</p>
											</div>
											{selectedUser?.id === user.id && (
												<Check className="h-4 w-4 text-primary" />
											)}
										</div>
									))}
								</div>
							</ScrollArea>
						)}

						{selectedUser && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Check className="h-4 w-4 text-green-500" />
								已选择: <span className="font-medium text-foreground">{selectedUser.nickname || selectedUser.username}</span>
							</div>
						)}
					</div>

					{/* 步骤 2: 选择 CRMEB 供应商 */}
					<div className="space-y-3">
						<Label className="flex items-center gap-2">
							<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">2</span>
							选择 CRMEB 供应商
						</Label>
						<div className="flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									placeholder="搜索供应商名称"
									value={supplierKeyword}
									onChange={(e) => setSupplierKeyword(e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && handleSearchSuppliers()}
									className="pl-9"
								/>
							</div>
							<Button variant="outline" onClick={handleSearchSuppliers} disabled={loadingSuppliers}>
								{loadingSuppliers ? <Loader2 className="h-4 w-4 animate-spin" /> : '搜索'}
							</Button>
						</div>

						{suppliers && suppliers.length > 0 && (
							<ScrollArea className="h-[140px] rounded-md border">
								<div className="p-2 space-y-1">
									{suppliers.map((supplier) => (
										<div
											key={supplier.id}
											onClick={() => setSelectedSupplier(supplier)}
											className={cn(
												"flex items-center gap-3 rounded-lg p-2 cursor-pointer transition-colors",
												selectedSupplier?.id === supplier.id
													? "bg-primary/10 border border-primary/30"
													: "hover:bg-muted"
											)}
                                        >
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
												<User className="h-4 w-4 text-muted-foreground" />
											</div>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium truncate">
													#{supplier.id} - {supplier.supplier_name}
												</p>
												<p className="text-xs text-muted-foreground">
													供应商 ID: {supplier.id}
												</p>
											</div>
											{selectedSupplier?.id === supplier.id && (
												<Check className="h-4 w-4 text-primary" />
											)}
                                        </div>
                                    ))}
                                </div>
							</ScrollArea>
						)}

						{selectedSupplier && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Check className="h-4 w-4 text-green-500" />
								已选择: <span className="font-medium text-foreground">#{selectedSupplier.id} {selectedSupplier.supplier_name}</span>
							</div>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						取消
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!selectedUser || !selectedSupplier || submitting}
					>
						{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						确认绑定
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
